import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { DashboardSetupStep } from '../setup/dashboardSetupStatus';
import { previouslyReady, type DashboardSetupState } from './useDashboardSetup';
import './DashboardSetupCard.css';

type Props = { setup: DashboardSetupState };

// Rendered as a page secondary action, so it must stay a direct child of <s-page>.
export function DashboardSetupStatus({ setup: { loading, failed, status } }: Props) {
    const navigate = useNavigate();
    const state = loading ? 'checking' : failed || !status || status.unverified ? 'unverified' : status.ready ? 'ready' : 'attention';
    const label = state === 'checking' ? 'Checking setup…' : state === 'ready' ? 'Setup ready' : state === 'attention' ? 'Setup needs attention' : 'Setup unverified';
    return (
        <s-button slot="secondary-actions" disabled={loading}
            icon={state === 'checking' ? undefined : state === 'ready' ? 'check-circle' : 'alert-triangle'}
            accessibilityLabel={`Setup status: ${label}. Open Setup & installation`}
            onClick={() => navigate('/setup-health')}>
            {label}
        </s-button>
    );
}

export default function DashboardSetupCard({ setup }: Props) {
    const navigate = useNavigate();
    const [showCompleted, setShowCompleted] = useState(false);
    const { health, status, loading, failed, showCard, retry, openThemeEditor } = setup;
    if (!showCard) return null;
    const openSetup = () => navigate('/setup-health');

    if (!status || status.pending.length === 0) {
        return (
            <s-banner tone="warning" heading="Setup status could not be verified">
                <s-button slot="secondary-actions" loading={loading} onClick={retry}>Recheck setup</s-button>
                <s-button slot="secondary-actions" onClick={openSetup}>Setup &amp; installation</s-button>
            </s-banner>
        );
    }

    const needsReview = status.coreComplete || previouslyReady(health!.shopDomain);
    const renderStep = (step: DashboardSetupStep, complete = false) => {
        const label = complete ? step.completedLabel : step.label.replace(/ to your published theme$/, '');
        const row = (
            <s-stack direction="inline" gap="small-200" alignItems="center">
                <s-icon type={complete ? 'check-circle' : 'circle-dashed'} tone={complete ? 'success' : 'auto'} color={complete ? 'base' : 'subdued'} />
                <s-text>{label}</s-text>
            </s-stack>
        );
        return (
            <s-clickable key={step.id} paddingBlock="small-200" paddingInline="small-100" borderRadius="base"
                href={step.url} target={step.external ? '_blank' : undefined}
                accessibilityLabel={step.external ? `${label} (opens theme editor in a new tab)` : undefined}
                onClick={event => {
                    if (step.external) {
                        openThemeEditor();
                        return;
                    }
                    // Modified clicks keep the browser's open-in-new-tab behavior.
                    const mouse = event as unknown as MouseEvent;
                    if (mouse.button !== 0 || mouse.metaKey || mouse.ctrlKey || mouse.shiftKey || mouse.altKey) return;
                    event.preventDefault();
                    navigate(step.url);
                }}>
                <s-grid gridTemplateColumns="1fr auto" alignItems="center" gap="small-200">
                    {row}
                    <s-icon type={step.external ? 'external' : 'chevron-right'} color="subdued" />
                </s-grid>
            </s-clickable>
        );
    };
    const remaining = `${status.pending.length} ${needsReview
        ? status.pending.length === 1 ? 'issue' : 'issues'
        : status.pending.length === 1 ? 'step left' : 'steps left'}`;

    return (
        <s-section accessibilityLabel="Setup" padding="none">
            <s-box padding="base" background="subdued">
                <s-stack gap="base">
                    <s-grid gridTemplateColumns="1fr auto" gap="base" alignItems="start">
                        <s-stack gap="small-300">
                            <s-heading>{needsReview ? 'Your setup needs attention' : 'Finish setting up PartFits'}</s-heading>
                            <s-text color="subdued">{needsReview ? 'Review these items to keep your storefront ready.' : 'Complete these steps to get your storefront ready.'}</s-text>
                        </s-stack>
                        <s-badge tone="warning">{remaining}</s-badge>
                    </s-grid>
                    <s-stack direction="inline" gap="small-200" alignItems="center">
                        <div className="pf-dashboard-setup__meter" role="progressbar" aria-label={`${status.complete} of ${status.total} required checks complete`}
                            aria-valuemin={0} aria-valuemax={status.total} aria-valuenow={status.complete}>
                            {status.steps.map((step, index) => <span key={step.id} className={index < status.complete ? 'pf-dashboard-setup__segment--complete' : undefined} />)}
                        </div>
                        <s-text color="subdued">{status.complete} of {status.total} required checks complete</s-text>
                    </s-stack>
                </s-stack>
            </s-box>
            <s-divider />

            <s-box padding="small-200">
                <s-stack gap="none">
                    {status.pending.map(step => renderStep(step))}
                </s-stack>
            </s-box>

            {(status.unverified || failed) && (
                <s-box paddingInline="base" paddingBlockEnd="small-200">
                    <s-stack direction="inline" gap="small-200" alignItems="center">
                        <s-text color="subdued">{failed ? 'Setup could not be refreshed. Showing the last checked results.' : 'Published theme checks are still unverified.'}</s-text>
                        <s-button variant="tertiary" loading={loading} onClick={retry}>Recheck setup</s-button>
                    </s-stack>
                </s-box>
            )}

            <s-divider />
            <s-box padding="small-200" background="subdued">
                <s-grid gridTemplateColumns="1fr auto" gap="base" alignItems="center">
                    <s-stack direction="inline">
                        <s-button variant="tertiary" icon={showCompleted ? 'chevron-up' : 'chevron-down'}
                            onClick={() => setShowCompleted(value => !value)}>
                            {`${status.complete} completed checks`}
                        </s-button>
                    </s-stack>
                    <s-text color="subdued">Published theme</s-text>
                </s-grid>
                {showCompleted && (
                    <s-stack gap="none">
                        {status.steps.filter(step => step.status === 'complete').map(step => renderStep(step, true))}
                    </s-stack>
                )}
            </s-box>
        </s-section>
    );
}
